import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { isAiStudioEnabledByTenant } from '@ff-os/pdp-shared-redux/slices/tenantSlice';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { AiStudioButton } from '@ff-os/shared-react/components/ai-studio/AiStudioButton';
import { AiStudioWindow } from "@ff-os/shared-react/components/ai-studio/AiStudioWindow";
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { PhotoEditWindow } from '@ff-os/shared-react/components/photo-edit/photo-edit-window/PhotoEditWindow';
import { UploadMethodsInline } from '@ff-os/shared-react/components/upload-methods/UploadMethodsInline';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { WindowButton } from '@ff-os/shared-react/components/window/partials/WindowButton';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { Area } from '@ff-os/shared-utils/types/Area';
import { isAiStudioEnabledByVariableId, selectAiStudioAppIdByVariableId } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { Size } from 'ff-canvas';
import { selectClipById, selectCropBoxById, selectFileNameById, selectFilterById, selectImageSizeById, updateCropBox, updateFilter } from '../_redux';
import { createGiftUploadMethods } from './createGiftUploadMethods';
import { uploadImage } from './helpers/uploadImage';
import { PhotoEditPreview } from './preview/PhotoEditPreview';
import { UploadPreview } from './preview/UploadPreview';
import styles from './PhotoUploaderInputControl.module.scss';

export const _ModalPhotoInput = ({
    variableIds, imageSrc, fileName,
    hasFile, fileErrors, hasWhiteEdges,
    onChange, imageSize, activeCropBox,
    applyCropBox, clip,
    filter, updateFilter,
    onConfirm, uploadImage,
    enableAiStudio, aiStudioProduct, aiStudioStartupAppId,
    pluginId, telemetryCategory
}: Props) => {

    const { t } = useTranslation();

    const [isEditPhotoOpen, setIsEditPhotoOpen] = useState(false);
    const [isSwapPhotoOpen, setIsSwapPhotoOpen] = useState(false);
    const [isAiStudioOpen, setIsAiStudioOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(hasFile && !imageSrc);

    const giftUploadMethods = useMemo(() => {
        return createGiftUploadMethods({
            dropzone: { onChange: onChange, additionalErrors: fileErrors },
            externalUpload: { variableIds: variableIds }
        });
    }, [fileErrors, onChange, variableIds]);

    const onUpload = () => {
        setIsLoading(true);
        setIsEditPhotoOpen(true);
    };

    const openImageReplace = () => aiStudioProduct
        ? setIsAiStudioOpen(true)
        : setIsSwapPhotoOpen(true);

    useEffect(() => {
        imageSrc && setIsLoading(false);
    }, [imageSrc]);

    if (isLoading) {
        return (
            <AspectRatio width={4} height={1}>
                <Spinner overlaySize='full' spinnerSize='small' />
            </AspectRatio>
        );
    }

    return (
        <Grid direction='column' gap='s'>

            {(!hasFile && !aiStudioProduct) && (
                <Cell>
                    <UploadMethodsInline
                        onUpload={onUpload}
                        methods={giftUploadMethods} />
                </Cell>
            )}

            {(!hasFile && aiStudioProduct) && (
                <Cell>
                    <div className={styles.button}>
                        {enableAiStudio && (
                            <AiStudioButton onClick={() => setIsAiStudioOpen(true)} />
                        )}
                    </div>
                </Cell>
            )}


            {isAiStudioOpen && (
               <WindowStackItem>
                   <AiStudioWindow
                       pluginId={pluginId}
                       aiStudioStartupAppId={aiStudioStartupAppId}
                       telemetryCategory={telemetryCategory}
                       uploadFiles={(files: File[]) => uploadImage(files[0])}
                       onClose={() => setIsAiStudioOpen(false)} />
               </WindowStackItem>
            )}

            {imageSrc && (
                <>
                    <Cell>
                        <UploadPreview url={imageSrc} variableIds={variableIds} />
                    </Cell>
                    <Cell>
                        <WindowButton chevron
                            onClick={() => setIsEditPhotoOpen(true)}
                            icon={<Icon type='general-ui-crop' />}>
                            {t('image_uploader.edit')}
                        </WindowButton>

                        {isEditPhotoOpen && (
                            <WindowStackItem>
                                <PhotoEditWindow
                                    onConfirm={onConfirm}
                                    overlayUrl={imageSrc} save={applyCropBox}
                                    close={() => setIsEditPhotoOpen(false)}
                                    activeCropBox={activeCropBox} imageSize={imageSize}
                                    clip={clip} filter={filter} updateFilter={updateFilter}
                                    uploadFiles={uploadImage}
                                    enableAiStudio={enableAiStudio}
                                    fileName={fileName}
                                    pluginId={pluginId}
                                    aiStudioStartupAppId={aiStudioStartupAppId}
                                    telemetryCategory={telemetryCategory}>
                                    <AspectRatio width={imageSize.width} height={imageSize.height}>
                                        <PhotoEditPreview variableIds={variableIds} imageSize={imageSize} />
                                    </AspectRatio>
                                </PhotoEditWindow>
                            </WindowStackItem>
                        )}
                    </Cell>
                    <Cell>
                        <WindowButton
                            icon={<Icon type='general-ui-replace' />}
                            onClick={openImageReplace}>
                            {t('image_uploader.replace')}
                        </WindowButton>

                        <UploadMethodsWindow
                            onUpload={onUpload}
                            isOpen={isSwapPhotoOpen}
                            close={() => setIsSwapPhotoOpen(false)}
                            methods={giftUploadMethods} />
                    </Cell>
                </>
            )}

            {hasWhiteEdges && (
                <Cell>
                    <Alert type='info' icon='alert-triangle-exclamation'>
                        <>{t('image_uploader.has_white_edges')}</>
                    </Alert>
                </Cell>
            )}
        </Grid>
    );
};

type OwnProps = {
    uploadImage: (image: File) => void;
};

type Props = OwnProps & {

    variableIds: string[];

    imageSize: Size;
    activeCropBox: Area;
    applyCropBox: (cropBox: Area) => void;
    clip: Clip | undefined;

    filter: string | null;
    updateFilter: (filter: string | null) => void;

    imageSrc: string | undefined;
    fileName?: string;
    onConfirm?: () => void;

    fileErrors: UploadError[];
    hasWhiteEdges: boolean;
    hasFile: boolean;

    onChange: (files: File[]) => void;

    enableAiStudio?: boolean;
    aiStudioProduct?: boolean;
    aiStudioStartupAppId?: string;

    pluginId: string;
    telemetryCategory: string;
};

const mapStateToProps = (state: GiftPdpState, ownProps: { variableIds: string[] }) => ({
    imageSize: selectImageSizeById(state, ownProps.variableIds[0])!,
    activeCropBox: selectCropBoxById(state, ownProps.variableIds[0])!,
    clip: selectClipById(state, ownProps.variableIds[0]),
    filter: selectFilterById(state, ownProps.variableIds[0]) || null,
    enableAiStudio: isAiStudioEnabledByTenant(state),
    aiStudioProduct: isAiStudioEnabledByVariableId(state, ownProps.variableIds[0]),
    fileName: selectFileNameById(state, ownProps.variableIds[0]),
    pluginId: state.scene.pluginId,
    telemetryCategory: state.scene.options.telemetryCategory,
    aiStudioStartupAppId: selectAiStudioAppIdByVariableId(state, ownProps.variableIds[0])
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: { variableIds: string[] }) => ({
    uploadImage: (image: File) => dispatch(uploadImage(image, ownProps.variableIds[0])),
    applyCropBox: (cropBox: Area) => dispatch(updateCropBox(ownProps.variableIds, cropBox)),
    updateFilter: (filter: string | null) => dispatch(updateFilter(ownProps.variableIds, filter))
});

export const ModalPhotoInput = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ModalPhotoInput);